// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { FileBarChart, Download, RefreshCw, CircleAlert } from 'lucide-react'
import * as api from '../../api/client'
import { toCsv } from '../../lib/opsFormat'

/**
 * Reporting — on-demand operational summaries, exportable as CSV. First version covers the reports
 * the data already supports: cost-per-customer, AI usage, and ingest activity. (Scheduled delivery,
 * PDF, and compliance-report templates are a deliberate future step, not faked here.)
 */
export default function ReportingTab() {
  const [dash, setDash] = useState<api.OpsDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setErr(null)
    try { setDash(await api.getOpsDashboard(30)) }
    catch (e: any) { setErr(e?.message || 'Failed to load report data') }
    finally { setLoading(false) }
  }

  function downloadCsv(filename: string, rows: (string | number)[][]) {
    const csv = toCsv(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }

  const reports = [
    {
      id: 'cost-per-customer',
      title: 'Cost per customer',
      desc: 'Month-to-date tenant DB + AI cost for each pharmacy.',
      available: !!dash?.per_customer_cost?.customers.length,
      run: () => {
        const rows: (string | number)[][] = [['Customer', 'Slug', 'Tenant DB ($)', 'AI ($)', 'Total ($)']]
        dash!.per_customer_cost!.customers.forEach(c =>
          rows.push([c.name, c.slug || '', c.tenant_db_cost_usd.toFixed(2), c.ai_cost_usd.toFixed(2), c.total_cost_usd.toFixed(2)]))
        downloadCsv(`cost-per-customer-${today()}.csv`, rows)
      },
    },
    {
      id: 'cost-by-resource',
      title: 'Cost by resource',
      desc: 'Month-to-date Azure cost for each resource.',
      available: !!dash?.azure_cost?.by_resource?.length,
      run: () => {
        const rows: (string | number)[][] = [['Resource', 'Cost ($)']]
        dash!.azure_cost!.by_resource!.forEach(r => rows.push([r.resource, r.cost.toFixed(2)]))
        downloadCsv(`cost-by-resource-${today()}.csv`, rows)
      },
    },
    {
      id: 'ai-usage',
      title: 'AI usage by pharmacy',
      desc: 'Month-to-date CareRx AI tokens, calls, and cost per pharmacy.',
      available: !!dash?.ai.by_pharmacy.length,
      run: () => {
        const rows: (string | number)[][] = [['Pharmacy', 'Calls', 'Input tokens', 'Output tokens', 'Total tokens', 'Cost ($)', 'Monthly limit', '% of limit']]
        dash!.ai.by_pharmacy.forEach(p =>
          rows.push([p.name, p.calls, p.input_tokens, p.output_tokens, p.total_tokens, p.cost_usd.toFixed(2), p.monthly_token_limit ?? '', p.pct_of_limit ?? '']))
        downloadCsv(`ai-usage-${today()}.csv`, rows)
      },
    },
    {
      id: 'ingest-activity',
      title: 'Ingest activity',
      desc: 'Current parse + mirror status counts for the ingest pipeline.',
      available: !!dash?.ingest_queue,
      run: () => {
        const rows: (string | number)[][] = [['Category', 'Status', 'Count']]
        Object.entries(dash!.ingest_queue.parse_status).forEach(([k, v]) => rows.push(['parse', k, v]))
        Object.entries(dash!.ingest_queue.mirror_status).forEach(([k, v]) => rows.push(['mirror', k, v]))
        downloadCsv(`ingest-activity-${today()}.csv`, rows)
      },
    },
  ]

  return (
    <div style={{ maxWidth: 'var(--page-max, 900px)' }}>
      <div style={head}>
        <div>
          <h2 style={h2}><FileBarChart size={18} /> Reporting</h2>
          <p style={sub}>On-demand operational summaries · export as CSV</p>
        </div>
        <button onClick={load} disabled={loading} style={refreshBtn}>
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : undefined }} /> Refresh
        </button>
      </div>

      {err && <div style={errorBanner}><CircleAlert size={16} />{err}</div>}

      {loading ? (
        <div style={loadingBox}><RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
      ) : (
        <>
          <div style={grid}>
            {reports.map(r => (
              <div key={r.id} style={card}>
                <div style={cardTitle}>{r.title}</div>
                <div style={cardDesc}>{r.desc}</div>
                <button disabled={!r.available} onClick={r.run} style={{ ...dlBtn, ...(r.available ? null : dlBtnDisabled) }}>
                  <Download size={14} /> {r.available ? 'Download CSV' : 'No data'}
                </button>
              </div>
            ))}
          </div>
          <p style={note}>
            Reports reflect month-to-date data at time of download. Scheduled delivery, PDF export, and
            compliance-report templates are planned but not yet available.
          </p>
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

function today() { return new Date().toISOString().slice(0, 10) }

const head: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }
const h2: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 800, margin: 0, color: '#0f172a' }
const sub: React.CSSProperties = { fontSize: 13, color: '#64748b', margin: '4px 0 0' }
const refreshBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155', cursor: 'pointer' }
const errorBanner: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: 40, justifyContent: 'center', color: '#64748b' }
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }
const cardTitle: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: '#0f172a' }
const cardDesc: React.CSSProperties = { fontSize: 12.5, color: '#64748b', flex: 1, lineHeight: 1.5 }
const dlBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '8px 12px', borderRadius: 8, border: '1px solid #0891b2', background: '#0891b2', color: '#fff', cursor: 'pointer', marginTop: 4 }
const dlBtnDisabled: React.CSSProperties = { background: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0', cursor: 'not-allowed' }
const note: React.CSSProperties = { fontSize: 11.5, color: '#94a3b8', marginTop: 16, lineHeight: 1.5 }
